import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Truck,
  List,
  Grid3X3,
  Filter,
  Search,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Wrench,
  MapPin,
  Fuel,
  Calendar,
  Clock,
  Eye,
  Edit,
  Plus,
  ClipboardCheck,
  Upload,
  Trash2
} from 'lucide-react';
import VehicleProfilePage from './VehicleProfilePage';
import { VehicleForm } from '../components/vehicles';
import { useAuth } from '../contexts/AuthContext';
import vehicleService from '../services/vehicleService';
import driverService from '../services/driverService';

const VehicleListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { company } = useAuth();
  const companyId = company?.id || company?.companyId || null;
  const [view, setView] = useState('grid'); // 'table', 'grid', 'maintenance', 'profile', 'form', 'edit', 'inspect', 'upload'
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [showActionModal, setShowActionModal] = useState(null); // 'inspect', 'upload'
  const [activeTab, setActiveTab] = useState(null); // Tab to open when viewing vehicle profile
  const [filterType, setFilterType] = useState('all'); // 'all', 'truck', 'trailer', 'van'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'active', 'maintenance', 'out-of-service'
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [draftVehicleData, setDraftVehicleData] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  const cardSkeletonItems = Array.from({ length: 6 });

  const VehicleCardsSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {cardSkeletonItems.map((_, idx) => (
        <div
          key={`vehicle-card-skeleton-${idx}`}
          className="bg-white border border-slate-200/60 rounded-lg shadow-sm p-4 animate-pulse"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 bg-slate-200/70 rounded-lg" />
              <div>
                <div className="h-4 w-28 bg-slate-200/70 rounded" />
                <div className="mt-2 h-3 w-44 bg-slate-200/60 rounded" />
              </div>
            </div>
            <div className="h-6 w-16 bg-slate-200/70 rounded-full" />
          </div>

          <div className="mt-6 space-y-3">
            <div className="h-3 w-full bg-slate-200/60 rounded" />
            <div className="flex items-center space-x-3">
              <div className="h-3 flex-1 bg-slate-200/50 rounded" />
              <div className="h-3 w-20 bg-slate-200/50 rounded" />
            </div>
            <div className="flex items-center space-x-3">
              <div className="h-3 flex-1 bg-slate-200/50 rounded" />
              <div className="h-3 w-24 bg-slate-200/50 rounded" />
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-2">
            <div className="h-9 w-10 bg-slate-200/60 rounded" />
            <div className="h-9 w-10 bg-slate-200/60 rounded" />
          </div>
        </div>
      ))}
    </div>
  );

  const VehicleTableSkeleton = () => (
    <div className="bg-white border border-slate-200/70 rounded-lg shadow-sm overflow-hidden">
      <div className="h-12 bg-slate-100/70 animate-pulse" />
      <div className="divide-y divide-slate-200/60">
        {Array.from({ length: 6 }).map((_, idx) => (
          <div key={`vehicle-table-skeleton-${idx}`} className="px-6 py-4 animate-pulse">
            <div className="flex items-center space-x-4">
              <div className="h-10 w-10 bg-slate-200/70 rounded" />
              <div className="flex-1 grid grid-cols-5 gap-4">
                <div className="h-4 bg-slate-200/70 rounded col-span-2" />
                <div className="h-4 bg-slate-200/60 rounded col-span-1" />
                <div className="h-4 bg-slate-200/60 rounded col-span-1" />
                <div className="h-4 bg-slate-200/60 rounded col-span-1" />
              </div>
              <div className="flex space-x-2">
                <div className="h-8 w-8 bg-slate-200/60 rounded" />
                <div className="h-8 w-8 bg-slate-200/60 rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
  const [driversById, setDriversById] = useState({});
  const [driversError, setDriversError] = useState(null);

  // Handle URL parameters for direct navigation to specific actions
  useEffect(() => {
    let paramsChanged = false;

    const action = searchParams.get('action');
    if (action) {
      switch (action) {
        case 'add':
          setView('form');
          setSelectedVehicleId(null);
          break;
        case 'inspect':
          setShowActionModal('inspect');
          break;
        case 'upload':
          setShowActionModal('upload');
          break;
        default:
          break;
      }
      searchParams.delete('action');
      paramsChanged = true;
    }

    const vehicleIdParam = searchParams.get('vehicleId');
    if (vehicleIdParam) {
      setSelectedVehicleId(vehicleIdParam);
      setView('profile');

      const tabParam = searchParams.get('tab');
      setActiveTab(tabParam || null);

      searchParams.delete('vehicleId');
      if (tabParam) {
        searchParams.delete('tab');
      }
      paramsChanged = true;
    }

    if (paramsChanged) {
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const fetchVehicles = useCallback(async () => {
    if (!companyId) {
      setVehicles([]);
      return;
    }

    setLoadingVehicles(true);
    setLoadError(null);

    try {
      const { vehicles: fetchedVehicles } = await vehicleService.listVehicles({ companyId });
      setVehicles(fetchedVehicles);
    } catch (error) {
      console.error('Failed to load vehicles:', error);
      setVehicles([]);
      setLoadError(error.getUserFriendlyMessage?.() || error.message || 'Failed to load vehicles');
    } finally {
      setLoadingVehicles(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  useEffect(() => {
    if (!companyId) {
      setDriversById({});
      return;
    }

    let isMounted = true;

    const fetchDrivers = async () => {
      setDriversError(null);

      try {
        const result = await driverService.listDrivers({}, { companyId });
        if (!isMounted) {
          return;
        }

        const driverMap = {};
        (result?.drivers || []).forEach((driver) => {
          if (driver?.id) {
            driverMap[driver.id] = driver;
          }
        });

        setDriversById(driverMap);
      } catch (error) {
        console.error('Failed to load drivers:', error);
        if (isMounted) {
          setDriversById({});
          setDriversError(error.getUserFriendlyMessage?.() || error.message || 'Failed to load drivers');
        }
      }
    };

    fetchDrivers();

    return () => {
      isMounted = false;
    };
  }, [companyId]);

  const resolvedVehicles = useMemo(() => (
    vehicles.map((vehicle) => {
      if (!vehicle.assignedDriverId) {
        return vehicle;
      }

      const driver = driversById[vehicle.assignedDriverId];
      if (!driver) {
        return vehicle;
      }

      const resolvedName = driver.name
        || [driver.firstName, driver.lastName].filter(Boolean).join(' ')
        || vehicle.assignedDriver
        || 'Assigned Driver';

      if (resolvedName === vehicle.assignedDriver) {
        return vehicle;
      }

      return {
        ...vehicle,
        assignedDriver: resolvedName
      };
    })
  ), [vehicles, driversById]);

  // Filter vehicles based on current filters
  const filteredVehicles = resolvedVehicles
    .filter(vehicle => {
      const matchesSearch = !searchQuery ||
        vehicle.vehicleNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.make.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.model.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (vehicle.assignedDriver || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (vehicle.licensePlate || '').toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType = filterType === 'all' || vehicle.type === filterType;
      const matchesStatus = filterStatus === 'all' || vehicle.status === filterStatus;

      return matchesSearch && matchesType && matchesStatus;
    })
    .sort((a, b) => {
      // Sort retired vehicles to the bottom
      const aIsRetired = a.status === 'retired';
      const bIsRetired = b.status === 'retired';

      if (aIsRetired && !bIsRetired) return 1;
      if (!aIsRetired && bIsRetired) return -1;
      return 0;
    });

  // Get status icon and color
  const getStatusDisplay = (status) => {
    const statusMap = {
      'active': { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', label: 'Active' },
      'maintenance': { icon: Wrench, color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Maintenance' },
      'out-of-service': { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Out of Service' }
    };
    return statusMap[status] || statusMap['active'];
  };

  // Get vehicle type icon
  const getTypeIcon = (type) => {
    const typeMap = {
      'truck': Truck,
      'trailer': Truck,
      'van': Truck
    };
    return typeMap[type] || Truck;
  };

  const toNumberOrNull = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);

  // Check if maintenance is due soon
  const isMaintenanceDue = (days) => {
    const value = toNumberOrNull(days);
    return value !== null && value <= 14 && value >= 0;
  };

  const isMaintenanceOverdue = (days) => {
    const value = toNumberOrNull(days);
    return value !== null && value < 0;
  };

  const formatDaysUntil = (days) => {
    const value = toNumberOrNull(days);
    if (value === null) {
      return 'No schedule';
    }
    if (value < 0) {
      return `${Math.abs(value)} days overdue`;
    }
    if (value === 0) {
      return 'Due today';
    }
    return `${value} days`;
  };

  const getSoonestDays = (...values) => {
    const numericValues = values
      .map(toNumberOrNull)
      .filter((value) => value !== null);
    if (numericValues.length === 0) {
      return null;
    }
    return Math.min(...numericValues);
  };

  // Get maintenance alert level
  const getMaintenanceAlert = (maintenanceDays, inspectionDays) => {
    if (isMaintenanceOverdue(maintenanceDays) || isMaintenanceOverdue(inspectionDays)) {
      return { level: 'critical', color: 'text-red-600', bg: 'bg-red-50' };
    }
    if (isMaintenanceDue(maintenanceDays) || isMaintenanceDue(inspectionDays)) {
      return { level: 'warning', color: 'text-yellow-600', bg: 'bg-yellow-50' };
    }
    return null;
  };

  // Navigation functions
  const handleViewVehicle = (vehicleId, tab = null) => {
    setSelectedVehicleId(vehicleId);
    setView('profile');
    // Store the tab parameter if provided
    if (tab) {
      setActiveTab(tab);
    }
  };

  const handleEditVehicle = (vehicleId) => {
    setSelectedVehicleId(vehicleId);
    setView('edit');
  };

  const handleAddVehicle = () => {
    setSelectedVehicleId(null);
    setSaveError(null);
    setView('form');
  };

  const handleDeleteVehicle = (vehicle) => {
    setDeleteConfirmation(vehicle);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation || !companyId) return;

    try {
      await vehicleService.deleteVehicle(deleteConfirmation.id, { companyId });

      // Refresh vehicle list
      await fetchVehicles();

      setDeleteConfirmation(null);
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      setLoadError(error.getUserFriendlyMessage?.() || error.message || 'Failed to delete vehicle');
      setDeleteConfirmation(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation(null);
  };

  const handleBackToList = () => {
    setSelectedVehicleId(null);
    setActiveTab(null);
    setSaveError(null);
    setView('table');
  };


  const handleFormSubmit = async (formData) => {
    if (!companyId) {
      const message = 'Company context missing. Please refresh or complete onboarding before adding vehicles.';
      setSaveError(message);
      // Throw so VehicleForm does not show its "created" confirmation.
      throw new Error(message);
    }

    try {
      setSaveError(null);

      if (view === 'edit' && selectedVehicleId) {
        const updatedVehicle = await vehicleService.updateVehicle(selectedVehicleId, formData, { companyId });
        setVehicles(prev => prev.map(vehicle => (
          vehicle.id === selectedVehicleId
            ? updatedVehicle
            : vehicle
        )));
        handleBackToList();
      } else {
        // Creating new vehicle
        const createdVehicle = await vehicleService.createVehicle(formData, { companyId });
        setVehicles(prev => [createdVehicle, ...prev]);
        setDraftVehicleData(null);

        // Store the newly created vehicle ID for potential document upload navigation
        setSelectedVehicleId(createdVehicle.id);
        // Form will handle navigation to documents tab internally
        return createdVehicle;
      }
    } catch (error) {
      console.error('Error saving vehicle:', error);
      setSaveError(error.getUserFriendlyMessage?.() || error.message || 'Failed to save vehicle');
      throw error;
    }
  };

  const handleDraftSave = useCallback((data) => {
    setDraftVehicleData(data);
  }, []);

  const handleStartUploadingDocuments = () => {
    // Navigate to vehicle profile documents tab
    if (selectedVehicleId) {
      setActiveTab('documents');
      setView('profile');
    }
  };

  const getCurrentVehicle = () => {
    return vehicles.find(vehicle => vehicle.id === selectedVehicleId);
  };

  // Table view component
  const TableView = () => (
    <div className="bg-white shadow-sm rounded-lg border">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Vehicle
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Driver
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Maintenance
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredVehicles.map((vehicle) => {
              const statusDisplay = getStatusDisplay(vehicle.status);
              const TypeIcon = getTypeIcon(vehicle.type);
              const maintenanceAlert = getMaintenanceAlert(vehicle.maintenanceDays, vehicle.inspectionDays);
              
              return (
                <tr key={vehicle.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <TypeIcon className="h-8 w-8 text-gray-400 mr-3" />
                      <div>
                        <button
                          onClick={() => handleViewVehicle(vehicle.id)}
                          className="text-left hover:bg-blue-50 rounded-md p-1 -m-1 transition-colors group"
                        >
                          <div className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                            {vehicle.vehicleNumber}
                          </div>
                          <div className="text-sm text-gray-500 group-hover:text-blue-500 transition-colors">
                            {vehicle.year} {vehicle.make} {vehicle.model}
                          </div>
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <statusDisplay.icon className={`h-4 w-4 mr-2 ${statusDisplay.color}`} />
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusDisplay.bg} ${statusDisplay.color}`}>
                        {statusDisplay.label}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {vehicle.assignedDriver}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900">
                      <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                      {vehicle.location}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <div className={`flex items-center text-sm ${maintenanceAlert ? maintenanceAlert.color : 'text-gray-500'}`}>
                        {maintenanceAlert && <AlertTriangle className="h-4 w-4 mr-1" />}
                        <span>
                          Maintenance: {formatDaysUntil(vehicle.maintenanceDays)}
                        </span>
                      </div>
                      <div className="flex items-center text-xs text-gray-500">
                        <Calendar className="h-3 w-3 mr-1" />
                        Inspection: {formatDaysUntil(vehicle.inspectionDays)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleViewVehicle(vehicle.id)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {vehicle.status !== 'retired' && (
                        <>
                          <button
                            onClick={() => handleEditVehicle(vehicle.id)}
                            className="text-gray-600 hover:text-gray-900"
                            title="Edit Vehicle"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteVehicle(vehicle)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Vehicle"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Grid view component
  const GridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {filteredVehicles.map((vehicle) => {
        const statusDisplay = getStatusDisplay(vehicle.status);
        const TypeIcon = getTypeIcon(vehicle.type);
        const maintenanceAlert = getMaintenanceAlert(vehicle.maintenanceDays, vehicle.inspectionDays);
        
        return (
          <div key={vehicle.id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <TypeIcon className="h-8 w-8 text-gray-400 mr-3" />
                  <div>
                    <button
                      onClick={() => handleViewVehicle(vehicle.id)}
                      className="text-left hover:bg-blue-50 rounded-md p-1 -m-1 transition-colors group"
                    >
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                        {vehicle.vehicleNumber}
                      </h3>
                      <p className="text-sm text-gray-500 group-hover:text-blue-500 transition-colors">
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </p>
                    </button>
                  </div>
                </div>
                <div className="flex items-center">
                  <statusDisplay.icon className={`h-4 w-4 mr-2 ${statusDisplay.color}`} />
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusDisplay.bg} ${statusDisplay.color}`}>
                    {statusDisplay.label}
                  </span>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              <div className="space-y-3">
                {/* Driver and Location */}
                <div>
                  <div className="text-sm text-gray-900 font-medium">{vehicle.assignedDriver}</div>
                  <div className="flex items-center text-sm text-gray-500">
                    <MapPin className="h-4 w-4 mr-1" />
                    {vehicle.location}
                  </div>
                </div>

                {/* Fuel Level (for trucks and vans) */}
                {vehicle.fuelLevel !== null && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-600">
                      <Fuel className="h-4 w-4 mr-2" />
                      Fuel Level
                    </div>
                    <div className="text-sm font-medium">{vehicle.fuelLevel}%</div>
                  </div>
                )}

                {/* Mileage */}
                {vehicle.mileage && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Mileage</span>
                    <span className="text-sm font-medium">{vehicle.mileage.toLocaleString()} mi</span>
                  </div>
                )}

                {/* Maintenance Alerts */}
                {maintenanceAlert && (
                  <div className={`p-3 rounded-lg ${maintenanceAlert.bg} border border-current border-opacity-20`}>
                    <div className={`flex items-center ${maintenanceAlert.color}`}>
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      <span className="text-sm font-medium">Maintenance Alert</span>
                    </div>
                    <div className="mt-1 text-xs text-gray-600">
                      {isMaintenanceOverdue(vehicle.maintenanceDays) && (
                        <div>Maintenance {formatDaysUntil(vehicle.maintenanceDays)}</div>
                      )}
                      {!isMaintenanceOverdue(vehicle.maintenanceDays) && isMaintenanceDue(vehicle.maintenanceDays) && (
                        <div>Maintenance due in {formatDaysUntil(vehicle.maintenanceDays)}</div>
                      )}
                      {isMaintenanceOverdue(vehicle.inspectionDays) && (
                        <div>Inspection {formatDaysUntil(vehicle.inspectionDays)}</div>
                      )}
                      {!isMaintenanceOverdue(vehicle.inspectionDays) && isMaintenanceDue(vehicle.inspectionDays) && (
                        <div>Inspection due in {formatDaysUntil(vehicle.inspectionDays)}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Regular maintenance info when no alerts */}
                {!maintenanceAlert && (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Next Maintenance</span>
                      <span className="text-gray-900">{formatDaysUntil(vehicle.maintenanceDays)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Next Inspection</span>
                      <span className="text-gray-900">{formatDaysUntil(vehicle.inspectionDays)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="px-4 py-3 border-t border-gray-200">
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => handleViewVehicle(vehicle.id)}
                  className="inline-flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View
                </button>
                {vehicle.status !== 'retired' && (
                  <>
                    <button
                      onClick={() => handleEditVehicle(vehicle.id)}
                      className="inline-flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteVehicle(vehicle)}
                      className="inline-flex items-center px-3 py-1 text-sm text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // Fleet Maintenance View component
  const FleetMaintenanceView = () => {
    // Get all vehicles that need maintenance attention
    const maintenanceAlerts = resolvedVehicles.filter(vehicle => {
      const alert = getMaintenanceAlert(vehicle.maintenanceDays, vehicle.inspectionDays);
      return alert !== null;
    });

    const criticalVehicles = resolvedVehicles.filter(v => 
      getMaintenanceAlert(v.maintenanceDays, v.inspectionDays)?.level === 'critical'
    );
    
    const warningVehicles = resolvedVehicles.filter(v => 
      getMaintenanceAlert(v.maintenanceDays, v.inspectionDays)?.level === 'warning'
    );

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Fleet Maintenance Overview</h3>
          
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <div className="flex items-center">
                <AlertTriangle className="h-8 w-8 text-red-600 mr-3" />
                <div>
                  <div className="text-2xl font-bold text-red-900">{criticalVehicles.length}</div>
                  <div className="text-sm text-red-700">Critical</div>
                </div>
              </div>
            </div>
            
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-yellow-600 mr-3" />
                <div>
                  <div className="text-2xl font-bold text-yellow-900">{warningVehicles.length}</div>
                  <div className="text-sm text-yellow-700">Warning</div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-center">
                <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
                <div>
                  <div className="text-2xl font-bold text-green-900">
                    {resolvedVehicles.length - maintenanceAlerts.length}
                  </div>
                  <div className="text-sm text-green-700">Up to Date</div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center">
                <Truck className="h-8 w-8 text-gray-600 mr-3" />
                <div>
                  <div className="text-2xl font-bold text-gray-900">{resolvedVehicles.length}</div>
                  <div className="text-sm text-gray-700">Total Fleet</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Critical Maintenance */}
        {criticalVehicles.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="px-6 py-4 border-b border-gray-200 bg-red-50">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                <h4 className="text-lg font-semibold text-red-900">Critical Maintenance Required</h4>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Issue</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days Overdue</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Driver</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {criticalVehicles.map((vehicle) => {
                    const overdueDays = getSoonestDays(
                      isMaintenanceOverdue(vehicle.maintenanceDays) ? vehicle.maintenanceDays : null,
                      isMaintenanceOverdue(vehicle.inspectionDays) ? vehicle.inspectionDays : null
                    );

                    return (
                    <tr key={vehicle.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Truck className="h-5 w-5 text-gray-400 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{vehicle.vehicleNumber}</div>
                            <div className="text-sm text-gray-500">{vehicle.make} {vehicle.model}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isMaintenanceOverdue(vehicle.maintenanceDays) && (
                          <div className="text-sm text-red-600">Maintenance Overdue</div>
                        )}
                        {isMaintenanceOverdue(vehicle.inspectionDays) && (
                          <div className="text-sm text-red-600">Inspection Overdue</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {overdueDays !== null ? `${Math.abs(overdueDays)} days` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {vehicle.assignedDriver}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewVehicle(vehicle.id, 'maintenance')}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Warning Maintenance */}
        {warningVehicles.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="px-6 py-4 border-b border-gray-200 bg-yellow-50">
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-yellow-600 mr-2" />
                <h4 className="text-lg font-semibold text-yellow-900">Maintenance Due Soon</h4>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due In</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Driver</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {warningVehicles.map((vehicle) => {
                    const dueInDays = getSoonestDays(
                      isMaintenanceOverdue(vehicle.maintenanceDays) ? null : vehicle.maintenanceDays,
                      isMaintenanceOverdue(vehicle.inspectionDays) ? null : vehicle.inspectionDays
                    );

                    return (
                    <tr key={vehicle.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Truck className="h-5 w-5 text-gray-400 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{vehicle.vehicleNumber}</div>
                            <div className="text-sm text-gray-500">{vehicle.make} {vehicle.model}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isMaintenanceDue(vehicle.maintenanceDays) && !isMaintenanceOverdue(vehicle.maintenanceDays) && (
                          <div className="text-sm text-yellow-600">Maintenance</div>
                        )}
                        {isMaintenanceDue(vehicle.inspectionDays) && !isMaintenanceOverdue(vehicle.inspectionDays) && (
                          <div className="text-sm text-yellow-600">Inspection</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {dueInDays !== null ? formatDaysUntil(dueInDays) : 'No schedule'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {vehicle.assignedDriver}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewVehicle(vehicle.id, 'maintenance')}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* All Vehicles Maintenance Summary */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h4 className="text-lg font-semibold text-gray-900">Fleet Maintenance Summary</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Maintenance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Inspection</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Driver</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredVehicles.map((vehicle) => {
                  const statusDisplay = getStatusDisplay(vehicle.status);
                  
                  return (
                    <tr key={vehicle.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Truck className="h-5 w-5 text-gray-400 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{vehicle.vehicleNumber}</div>
                            <div className="text-sm text-gray-500">{vehicle.make} {vehicle.model}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <statusDisplay.icon className={`h-4 w-4 mr-2 ${statusDisplay.color}`} />
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusDisplay.bg} ${statusDisplay.color}`}>
                            {statusDisplay.label}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${isMaintenanceOverdue(vehicle.maintenanceDays) ? 'text-red-600 font-semibold' : isMaintenanceDue(vehicle.maintenanceDays) ? 'text-yellow-600 font-medium' : 'text-gray-900'}`}>
                          {formatDaysUntil(vehicle.maintenanceDays)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${isMaintenanceOverdue(vehicle.inspectionDays) ? 'text-red-600 font-semibold' : isMaintenanceDue(vehicle.inspectionDays) ? 'text-yellow-600 font-medium' : 'text-gray-900'}`}>
                          {formatDaysUntil(vehicle.inspectionDays)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {vehicle.assignedDriver}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewVehicle(vehicle.id, 'maintenance')}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Show vehicle profile if selected
  if (view === 'profile' && selectedVehicleId) {
    return (
      <VehicleProfilePage
        vehicleId={selectedVehicleId}
        onBack={handleBackToList}
        initialTab={activeTab}
      />
    );
  }

  // Show vehicle form for adding new vehicle
  if (view === 'form') {
    return (
      <div className="-mx-4 -my-8 sm:-mx-6 lg:-mx-8 h-[calc(100vh-4rem)] flex flex-col bg-slate-50">
        <div className="sticky top-0 z-1 bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleBackToList}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Vehicle List
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Add New Vehicle</h1>
                  <p className="text-sm text-gray-600 mt-1">Create a new vehicle profile</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {saveError && (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
              <div className="rounded-md bg-red-50 border border-red-200 p-4 mb-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <svg className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-red-700 font-medium">{saveError}</p>
                  </div>
                  <button
                    onClick={() => setSaveError(null)}
                    className="text-red-400 hover:text-red-600 ml-4"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
          <VehicleForm
            initialValues={draftVehicleData}
            onSubmit={handleFormSubmit}
            onCancel={handleBackToList}
            onSaveDraft={handleDraftSave}
            onStartUploadingDocuments={handleStartUploadingDocuments}
          />
        </div>
      </div>
    );
  }

  // Show vehicle form for editing existing vehicle
  if (view === 'edit' && selectedVehicleId) {
    const currentVehicle = getCurrentVehicle();
    return (
      <div className="-mx-4 -my-8 sm:-mx-6 lg:-mx-8 h-[calc(100vh-4rem)] flex flex-col bg-slate-50">
        <div className="sticky top-0 z-1 bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleBackToList}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Vehicle List
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Edit {currentVehicle?.vehicleNumber || 'Vehicle'}</h1>
                  <p className="text-sm text-gray-600 mt-1">Update vehicle information</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {saveError && (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
              <div className="rounded-md bg-red-50 border border-red-200 p-4 mb-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <svg className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-red-700 font-medium">{saveError}</p>
                  </div>
                  <button
                    onClick={() => setSaveError(null)}
                    className="text-red-400 hover:text-red-600 ml-4"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
          <VehicleForm
            vehicle={currentVehicle}
            onSubmit={handleFormSubmit}
            onCancel={handleBackToList}
          />
        </div>
      </div>
    );
  }

  if (loadingVehicles && resolvedVehicles.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-3 sm:space-y-0">
          <div>
            <h2 className="text-3xl font-bold text-slate-900">Vehicle Management</h2>
            <p className="text-slate-600 mt-2">Manage your fleet vehicles</p>
          </div>
          <div className="flex items-center space-x-3">
            {view === 'maintenance' ? (
              <button 
                onClick={() => setView('grid')}
                className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Truck className="h-4 w-4 mr-2" />
                Back to Vehicles
              </button>
            ) : (
              <button 
                onClick={() => setView('maintenance')}
                className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
              >
                <Wrench className="h-4 w-4 mr-2" />
                Fleet Maintenance
              </button>
            )}
            <button 
              onClick={handleAddVehicle}
              className="btn-primary transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Vehicle
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search vehicles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full lg:w-80"
            />
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </button>
            </div>

            {view !== 'maintenance' && (
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setView('table')}
                  className={`p-2 rounded-md transition-colors ${
                    view === 'table' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Table View"
                >
                  <List className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setView('grid')}
                  className={`p-2 rounded-md transition-colors ${
                    view === 'grid' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Grid View"
                >
                  <Grid3X3 className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="pt-2 space-y-6">
          {view === 'table' ? <VehicleTableSkeleton /> : <VehicleCardsSkeleton />}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={cancelDelete}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    Delete Vehicle
                  </h3>
                  <div className="mt-2 space-y-3">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete <span className="font-semibold text-gray-900">{deleteConfirmation.vehicleNumber}</span>? This action will retire the vehicle.
                    </p>

                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-yellow-800">
                            Important: Vehicle Documents
                          </h4>
                          <div className="mt-2 text-sm text-yellow-700">
                            <p className="mb-2">
                              Vehicle documents will be <strong>permanently deleted in 30 days</strong> to give you time to download them.
                            </p>
                            <p className="text-xs text-yellow-600">
                              Note: You can manually delete documents permanently at any time from the vehicle's profile.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Delete Vehicle
                </button>
                <button
                  type="button"
                  onClick={cancelDelete}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-3 sm:space-y-0">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">Vehicle Management</h2>
          <p className="text-slate-600 mt-2">Manage your fleet vehicles</p>
        </div>
        <div className="flex items-center space-x-3">
          {view === 'maintenance' ? (
            <button 
              onClick={() => setView('grid')}
              className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Truck className="h-4 w-4 mr-2" />
              Back to Vehicles
            </button>
          ) : (
            <button 
              onClick={() => setView('maintenance')}
              className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
            >
              <Wrench className="h-4 w-4 mr-2" />
              Fleet Maintenance
            </button>
          )}
          <button 
            onClick={handleAddVehicle}
            className="btn-primary transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Vehicle
          </button>
        </div>
      </div>
      
      {loadError && (
        <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          {loadError}
        </div>
      )}

      {driversError && (
        <div className="mt-3 rounded-md bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-700">
          {driversError}
        </div>
      )}

      {!companyId && (
        <div className="mt-3 rounded-md border border-dashed border-gray-300 bg-white p-6 text-sm text-gray-600">
          Connect your company profile to manage vehicles. Complete onboarding or contact your administrator for access.
        </div>
      )}

      {loadingVehicles && resolvedVehicles.length === 0 && (
        <div className="space-y-6">
          {view === 'table' ? <VehicleTableSkeleton /> : <VehicleCardsSkeleton />}
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search vehicles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full lg:w-80"
          />
        </div>

        <div className="flex items-center space-x-4">
          {/* Filters */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </button>
            
            {/* Quick filter badges */}
            {(filterType !== 'all' || filterStatus !== 'all') && (
              <div className="flex items-center space-x-2">
                {filterType !== 'all' && (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                    {filterType}
                    <button 
                      onClick={() => setFilterType('all')}
                      className="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                )}
                {filterStatus !== 'all' && (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                    {filterStatus}
                    <button 
                      onClick={() => setFilterStatus('all')}
                      className="ml-1 text-green-600 hover:text-green-800"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* View Toggle */}
          {view === 'maintenance' ? (
            <button
              onClick={() => setView('grid')}
              className="inline-flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
            >
              <Truck className="h-4 w-4 mr-2" />
              Back to Vehicles
            </button>
          ) : (
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setView('table')}
                className={`p-2 rounded-md transition-colors ${
                  view === 'table' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Table View"
              >
                <List className="h-4 w-4" />
              </button>
              <button
                onClick={() => setView('grid')}
                className={`p-2 rounded-md transition-colors ${
                  view === 'grid' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Grid View"
              >
                <Grid3X3 className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-white p-4 rounded-lg border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="truck">Trucks</option>
                <option value="trailer">Trailers</option>
                <option value="van">Vans</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="maintenance">Maintenance</option>
                <option value="out-of-service">Out of Service</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>Showing {filteredVehicles.length} of {resolvedVehicles.length} vehicles</span>
        <div className="flex items-center space-x-4">
          <span className="flex items-center">
            <div className="w-2 h-2 bg-red-400 rounded-full mr-2"></div>
            {resolvedVehicles.filter(v => getMaintenanceAlert(v.maintenanceDays, v.inspectionDays)?.level === 'critical').length} Critical
          </span>
          <span className="flex items-center">
            <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
            {resolvedVehicles.filter(v => getMaintenanceAlert(v.maintenanceDays, v.inspectionDays)?.level === 'warning').length} Warning
          </span>
        </div>
      </div>

      {/* Main Content */}
      {view === 'table' ? <TableView /> : view === 'grid' ? <GridView /> : view === 'maintenance' ? <FleetMaintenanceView /> : <GridView />}

      {/* Empty State */}
      {filteredVehicles.length === 0 && view !== 'maintenance' && (
        <div className="text-center py-12">
          <Truck className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No vehicles found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchQuery || filterType !== 'all' || filterStatus !== 'all' 
              ? 'Try adjusting your search or filters.'
              : 'Get started by adding your first vehicle.'
            }
          </p>
        </div>
      )}

      {/* Action Modals */}
      {/* Vehicle Selection Modal for Inspection */}
      {showActionModal === 'inspect' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <ClipboardCheck className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Start Vehicle Inspection</h3>
              </div>
              <button
                onClick={() => setShowActionModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">Select a vehicle to inspect:</p>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {resolvedVehicles.filter(v => v.status === 'active').map((vehicle) => (
                  <button
                    key={vehicle.id}
                    onClick={() => {
                      handleViewVehicle(vehicle.id, 'inspections');
                      setShowActionModal(null);
                    }}
                    className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{vehicle.vehicleNumber}</div>
                        <div className="text-sm text-gray-600">{vehicle.year} {vehicle.make} {vehicle.model}</div>
                        <div className="text-sm text-gray-500">{vehicle.location}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Driver: {vehicle.assignedDriver}</div>
                        <div className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusDisplay(vehicle.status).bg} ${getStatusDisplay(vehicle.status).color}`}>
                          {getStatusDisplay(vehicle.status).label}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {showActionModal === 'upload' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Upload className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Upload Document</h3>
              </div>
              <button
                onClick={() => setShowActionModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">Select a vehicle to upload documents for:</p>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {resolvedVehicles.map((vehicle) => (
                  <button
                    key={vehicle.id}
                    onClick={() => {
                      handleViewVehicle(vehicle.id, 'documents');
                      setShowActionModal(null);
                    }}
                    className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{vehicle.vehicleNumber}</div>
                        <div className="text-sm text-gray-600">{vehicle.year} {vehicle.make} {vehicle.model}</div>
                        <div className="text-sm text-gray-500">{vehicle.location}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Driver: {vehicle.assignedDriver}</div>
                        <div className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusDisplay(vehicle.status).bg} ${getStatusDisplay(vehicle.status).color}`}>
                          {getStatusDisplay(vehicle.status).label}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default VehicleListPage;
